import { reaction, set } from 'mobx';
import { ABTestStore } from './ABTestStore';
import { ActivityFeedStore } from './ActivityFeedStore';
import { CookieConsentStore } from './CookieConsentStore';
import { DrawHydration, DrawStore } from './DrawStore';
import {
  ENTER_STORE_LOCAL_STORAGE_KEY,
  EnterStore,
  EnterStoreHydration,
} from './EnterStore';
import { PrizeHydration, PrizeStore } from './PrizeStore';
import { ScrollStore } from './ScrollStore';
import { UIHydration, UIStore } from './UIStore';
import { UserHydration, UserStore } from './UserStore';
import { UTMHydration, UTMStore } from './UTMStore';
import { AllPrizesPageStore } from './AllPrizesPageStore';
import { ensureKey } from '@daymade/common-lib/types';

export type RootStoreHydration = {
  userStore?: UserHydration;
  UIStore?: UIHydration;
  drawStore?: DrawHydration;
  cookieConsentStore?: CookieConsentStore;
  prizeStore?: PrizeHydration;
  utmStore?: UTMHydration;
  enterStore?: EnterStoreHydration;
};

export class RootStore {
  userStore: UserStore;
  activityFeedStore: ActivityFeedStore;
  UIStore: UIStore;
  drawStore: DrawStore;
  enterStore: EnterStore;
  cookieConsentStore: CookieConsentStore;
  scrollStore: ScrollStore;
  prizeStore: PrizeStore;
  utmStore: UTMStore;
  abTestStore: ABTestStore;
  allPrizesPageStore: AllPrizesPageStore;
  initialized = false;

  constructor() {
    this.userStore = new UserStore(this);
    this.activityFeedStore = new ActivityFeedStore(this);
    this.UIStore = new UIStore(this);
    this.drawStore = new DrawStore(this);
    this.enterStore = new EnterStore(this);
    this.cookieConsentStore = new CookieConsentStore(this);
    this.scrollStore = new ScrollStore(this);
    this.prizeStore = new PrizeStore(this);
    this.utmStore = new UTMStore(this);
    this.abTestStore = new ABTestStore(this);
    this.allPrizesPageStore = new AllPrizesPageStore(this);

    if (typeof window !== 'undefined') {
      reaction(
        () => {
          const data = this.enterStore.getData();
          (data as Record<string, unknown>).lastModified = new Date();
          return JSON.stringify(data);
        },
        (json) => {
          if (this.initialized) {
            // Only save local storage after we've initialised, otherwise we end
            // up overwriting local storage with values used during initialisation
            this.saveStoreLocalStorage(ENTER_STORE_LOCAL_STORAGE_KEY, json);
          }
        },
      );
    }
  }

  hydrate(data: RootStoreHydration) {
    if (data.userStore) {
      this.userStore.hydrate(data.userStore);
    }
    if (data.UIStore) {
      this.UIStore.hydrate(data.UIStore);
    }
    if (data.drawStore) {
      this.drawStore.hydrate(data.drawStore);
    }
    if (data.cookieConsentStore) {
      this.cookieConsentStore.hydrate(data.cookieConsentStore);
    }
    if (data.prizeStore) {
      this.prizeStore.hydrate(data.prizeStore);
    }
    if (data.utmStore) {
      this.utmStore.hydrate(data.utmStore);
    }
    if (data.enterStore) {
      this.enterStore.hydrate(data.enterStore);
    }
  }

  loadEnterStoreLocalStorage = (store: EnterStore, name: string) => {
    const jsonString = localStorage.getItem(name);
    const loadedSettings = jsonString ? JSON.parse(jsonString) : undefined;

    if (!loadedSettings) {
      return;
    }

    // A store should be invalidated after a period of time, or if the store does not have a last modified date. At the
    // time of writing, the only store that was saved was the EnterStore. If other stores need to be saved, then we
    // should consider if they need the expiration behaviour
    const lastModifiedDateMillis = new Date(
      loadedSettings.lastModified,
    ).getTime();
    if (isNaN(lastModifiedDateMillis) || lastModifiedDateMillis === 0) {
      return;
    }

    const timeDifference = new Date().getTime() - lastModifiedDateMillis;
    // Expiry is after 12 hours
    const hasExpired = timeDifference > 12 * 3600000;

    if (hasExpired) {
      return;
    }

    if (name === ENTER_STORE_LOCAL_STORAGE_KEY) {
      // Some older users may have these value in their localStorage for enter store. Do not restore them
      delete loadedSettings[
        ensureKey<EnterStore>(undefined, 'adyenPaymentSession')
      ];
      delete loadedSettings[ensureKey<EnterStore>(undefined, 'orderId')];
    }

    set(store, loadedSettings);
  };

  saveStoreLocalStorage = (name: string, json: string) => {
    localStorage.setItem(name, json);
  };
}
